
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {getSelectedDateRange} from 'redux/modules/currentUser/selectors'
import {
  setActiveFilter,
  setSessionFilter,
  setSessionsOnly,
  resetActiveFilters,
  resetFiltersAndStats,
  fetchOrdersWithStats
} from 'redux/modules/orders/actions'
import {
  getOrders,
  getOrdersStats,
  getFiltersData,
  getOrdersPageCurrent,
  getOrdersPageCount,
  getSelectedGroups
} from 'redux/modules/orders/selectors'
import {CSSTransitionGroup} from 'react-transition-group'
import OrdersList from './OrdersList'
import OrdersFilter from './OrdersFilter'
import Icon from 'components/Icon'
import {Table, Column, Cell} from 'components/Table'
import WithTooltip from 'components/WithTooltip'
import Link from 'components/Link'
import Limit from 'containers/Limit'
import UpgradePanel from 'components/UpgradePanel'
import propsChanged from 'utils/propsChanged'
import distanceInWords from 'date-fns/distance_in_words'
import addMilliseconds from 'date-fns/add_milliseconds'
import partial from 'utils/partial'
import classnames from 'classnames'

const LIMITED_ORDERS_AMOUNT = 5

export class OrdersView extends React.Component {
  static propTypes = {
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    orders: PropTypes.array,
    orderStats: PropTypes.array,
    ordersMaxPage: PropTypes.number,
    ordersCurrentPage: PropTypes.number,
    fetchOrdersWithStats: PropTypes.func,
    isOrdersFetching: PropTypes.bool,
    selectedGroups: PropTypes.object,
    filters: PropTypes.object,
    sessions: PropTypes.object,
    filtersData: PropTypes.object,
    setActiveFilter: PropTypes.func,
    setSessionFilter: PropTypes.func,
    setSessionsOnly: PropTypes.func,
    sessionsOnly: PropTypes.bool,
    resetActiveFilters: PropTypes.func,
    resetFiltersAndStats: PropTypes.func,
    isOrdersFiltered: PropTypes.bool,
    countries: PropTypes.array,
  }

  static contextTypes = {
    permissions: PropTypes.object,
    currency: PropTypes.string,
    timezone: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.state = {
      statsOpened: true
    }
  }

  componentDidMount() {
    const {currency, timezone} = this.context
    const {fetchOrdersWithStats, startDate, endDate, filters, sessions} = this.props
    fetchOrdersWithStats(0, startDate, endDate, currency, timezone, filters, sessions)
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const {fetchOrdersWithStats, resetFiltersAndStats} = this.props
    const {startDate, endDate, filters, sessions, sessionsOnly} = nextProps
    const {currency, timezone} = nextContext

    if (propsChanged(this.props, nextProps, ['startDate', 'endDate'])) {
      resetFiltersAndStats()
      fetchOrdersWithStats(0, startDate, endDate, currency, timezone, sessionsOnly)
    } else if (propsChanged(this.props, nextProps, ['filters', 'sessions', 'sessionsOnly'])) {
      fetchOrdersWithStats(0, startDate, endDate, currency, timezone, filters, sessions, sessionsOnly)
    }
  }

  render() {
    const {
      orders,
      isOrdersFetching,
      ordersCurrentPage,
      ordersMaxPage,
      selectedGroups,
      filtersData,
      filters,
      sessions,
      resetActiveFilters,
      sessionsOnly,
      setSessionsOnly,
      countries
    } = this.props

    const {
      permissions
    } = this.context

    const featureActive = permissions.read.includes('@features/orderExplorer')
    const featureConversionsStatistic = true

    return (
      <div className='orders-view'>
        <Limit
          limitKey='orderExplorer'
          message={
            <UpgradePanel>
              <p>Sorry but our awesome filtering options are only available on a paid plan. Learn more about them
                {' '}
                <Link to='/path/to/ordersExplorer' className='orders-view_upgrade-link'>
                  here
                </Link>.
              </p>
            </UpgradePanel>
          }
          bigIcon
        >
          <OrdersFilter
            disabled={!featureActive}
            filters={filtersData}
            touchpoints={sessions}
            onFilterChange={this.onFilterChange}
            onFilterReset={resetActiveFilters}
            onTouchpointChange={this.onTouchpointChange}
            setSessionsOnly={setSessionsOnly}
            sessionsOnly={sessionsOnly}
            countries={countries}
          />
        </Limit>
        {this.renderStats(!featureConversionsStatistic)}
        <OrdersList
          limitedIds={this.getLimitedOrders(!featureActive)}
          orders={orders}
          currentPage={ordersCurrentPage}
          maxPage={ordersMaxPage}
          onPageChange={this.onPageChange}
          loading={isOrdersFetching}
          selectedGroups={selectedGroups}
          filters={filters}
        />
      </div>
    )
  }

  renderStats(disabled) {
    const {statsOpened} = this.state
    const classNames = classnames('orders-stats', {
      'is-disabled': disabled
    })

    return (
      <div className={classNames}>
        <div className='orders-stats_header'>
          <div className='orders-stats_header_title'>Customer Journey Statistics</div>
          <div className='orders-stats_header_icon' onClick={this.onToggleStatsTable}>
            <Icon name={statsOpened ? 'fold' : 'expand'} size='14px' />
          </div>
        </div>
        <CSSTransitionGroup
          component='div'
          className='orders-stats_animated'
          transitionName='stats'
          transitionEnterTimeout={200}
          transitionLeaveTimeout={200}>
          {this.renderStatsTable(disabled)}
        </CSSTransitionGroup>
      </div>
    )
  }

  renderStatsTable(disabled) {
    const {orderStats, isOrdersFiltered, isOrdersFetching} = this.props
    const {statsOpened} = this.state

    if (!statsOpened) return null
    const classNames = classnames('orders-stats_table', {
      'is-filtered': isOrdersFiltered
    })

    return (
      <Table
        className={classNames}
        data={orderStats}
        empty='No statistics for chosen date period.'
        loading={isOrdersFetching}
        loadingRows={2}
        hover
      >
        <Column
          cell={statRow => {
            const {name} = statRow
            return (
              <Cell>
                {name === 'all' ? 'Total' : 'Filtered'}
              </Cell>
            )
          }}
          width='90px'
        />
        <Column
          header={<Cell align='right'>Number of Customer Journey</Cell>}
          cell={statRow => this.renderStatsTableRow(statRow.total, 'number', disabled)}
        />
        <Column
          header={<Cell align='right'>Journeys with post-view</Cell>}
          cell={statRow => this.renderStatsTableRow(statRow.impressionCount, 'number', disabled)}
        />
        <Column
          header={<WithTooltip disabled={disabled} title='Average number of visits' style='blue'>
            <div className='table-cell is-grow is-align-right'>Avg # of visits</div>
          </WithTooltip>}
          cell={statRow => this.renderStatsTableRow(statRow.avgSessions, 'decimal', disabled)}
        />
        <Column
          header={<Cell align='right'>Avg time to convert</Cell>}
          cell={partial(this.renderAvgTimeToBuy, disabled)}
        />
      </Table>
    )
  }

  renderStatsTableRow(value, format, disabled) {
    if (disabled) {
      return (
        <Cell align='right'>-</Cell>
      )
    }

    return (
      <Cell align='right' format={format}>
        {value}
      </Cell>
    )
  }

  renderAvgTimeToBuy(disabled, statRow) {
    const {avgTimeToBuy} = statRow
    const now = new Date()
    const newDate = addMilliseconds(now, avgTimeToBuy)

    if (disabled) {
      return (
        <Cell align='right'>-</Cell>
      )
    }

    return (
      <Cell align='right'>
        {distanceInWords(newDate, now, {includeSeconds: true})}
      </Cell>
    )
  }

  onToggleStatsTable = () => {
    const {statsOpened} = this.state
    this.setState({
      statsOpened: !statsOpened
    })
  }

  onPageChange = page => {
    const {currency, timezone} = this.context
    const {startDate, endDate, fetchOrdersWithStats, filters, sessions, sessionsOnly} = this.props

    fetchOrdersWithStats(page, startDate, endDate, currency, timezone, filters, sessions, sessionsOnly)
  }

  onFilterChange = (type, value) => {
    const {setActiveFilter} = this.props
    console.log('onFilterChange', type)
    console.log('onFilterChange', value)
    setActiveFilter && setActiveFilter(type, value)
  }

  onTouchpointChange = (type, value, channelTypes) => {
    const {setSessionFilter} = this.props
    if (!setSessionFilter) return null

    setSessionFilter(type, value, channelTypes)
  }

  getLimitedOrders(disabled) {
    const {orders, ordersCurrentPage} = this.props

    if (!disabled || !orders) return null

    if (ordersCurrentPage === 0) return orders.slice(0, LIMITED_ORDERS_AMOUNT).map(order => order.id)

    return []
  }
}

const mapStateToProps = state => {
  const {startDate, endDate} = getSelectedDateRange(state)
  const orderStats = getOrdersStats(state)
  const isOrdersFiltered = orderStats && orderStats.length === 2
  console.log('ss')

  return {
    startDate,
    endDate,
    orders: getOrders(state, startDate, endDate),
    orderStats,
    filtersData: getFiltersData(state),
    filters: state.orders.filters,
    sessions: state.orders.sessions,
    isOrdersFiltered,
    isOrdersFetching: state.orders.listFetching,
    isOrdersStatsFetching: state.orders.globalStatsFetching,
    ordersCurrentPage: getOrdersPageCurrent(state),
    ordersMaxPage: getOrdersPageCount(state, startDate, endDate),
    selectedGroups: getSelectedGroups(state),
    sessionsOnly: state.orders.sessionsOnly,
    countries: state.orders?.globalStats?.countries
  }
}

const mapDispatchToProps = {
  setActiveFilter,
  setSessionFilter,
  setSessionsOnly,
  resetActiveFilters,
  resetFiltersAndStats,
  fetchOrdersWithStats
}

export default connect(mapStateToProps, mapDispatchToProps)(OrdersView)



// WEBPACK FOOTER //
// ./client/views/OrdersInsightView/index.js